import { useCallback, useEffect, useState } from "react"
import { useSnackbar } from "../../context/snackbar"

const API = process.env.REACT_APP_API_URL

// "Oct 22, 2026, 9:14 PM" in Philippine time, whatever the viewer's computer is set to
const when = (d) =>
  new Date(d).toLocaleString("en-PH", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })

const VIEWS = [
  { key: "", label: "Inbox" },
  { key: "new", label: "New" },
  { key: "archived", label: "Archived" },
]

/** Admin → Inquiries: every message sent through the homepage Contact Us form */
export default function InquiriesPanel({ onCountChange }) {
  const { showSnackbar } = useSnackbar()
  const [view, setView] = useState("")
  const [items, setItems] = useState([])
  const [counts, setCounts] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(`${API}/inquiries${view ? `?status=${view}` : ""}`, { credentials: "include" })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || "Failed to load inquiries")
      setItems(data.inquiries)
      setCounts(data.counts || {})
      onCountChange?.(data.counts?.new || 0)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [view, onCountChange])

  useEffect(() => {
    load()
  }, [load])

  const setStatus = async (item, status) => {
    try {
      const res = await fetch(`${API}/inquiries/${item._id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      })
      if (!res.ok) throw new Error((await res.json()).message)
      await load()
    } catch (err) {
      showSnackbar(err.message || "Couldn't update", "error")
    }
  }

  const remove = async (item) => {
    if (!window.confirm(`Delete the inquiry from ${item.name}? This can't be undone.`)) return
    try {
      const res = await fetch(`${API}/inquiries/${item._id}`, { method: "DELETE", credentials: "include" })
      if (!res.ok) throw new Error((await res.json()).message)
      showSnackbar("Inquiry deleted", "success")
      await load()
    } catch (err) {
      showSnackbar(err.message || "Couldn't delete", "error")
    }
  }

  const replyLink = (item) =>
    `mailto:${encodeURIComponent(item.email)}?subject=${encodeURIComponent("Re: your message to Marafye")}`

  const total = (counts.new || 0) + (counts.read || 0)

  return (
    <div className="container my-4 text-start" style={{ maxWidth: 900 }}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h2 className="me-auto mb-0">Inquiries</h2>
        <button className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>
      <p className="text-muted">
        Messages from the Contact Us form on the homepage. Times are Philippine time. A copy of each is
        also emailed to admin@marafye.com.
      </p>

      {/* VIEW FILTER */}
      <div className="btn-group mb-3" role="group" aria-label="Show inquiries">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            className={`btn btn-sm ${view === v.key ? "btn-success" : "btn-outline-success"}`}
            aria-pressed={view === v.key}
            onClick={() => setView(v.key)}
          >
            {v.label}
            {v.key === "" && ` (${total})`}
            {v.key === "new" && ` (${counts.new || 0})`}
            {v.key === "archived" && ` (${counts.archived || 0})`}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="text-muted py-3">Loading…</div>}

      {!loading && !error && items.length === 0 && (
        <div className="border rounded-3 p-4 text-center text-muted">
          {view === "archived"
            ? "Nothing archived."
            : view === "new"
              ? "No new inquiries. You're all caught up."
              : "No inquiries yet. Messages from the Contact Us form will show up here."}
        </div>
      )}

      {!loading &&
        items.map((item) => {
          const isNew = item.status === "new"
          return (
            <article
              key={item._id}
              className={`border rounded-3 p-3 mb-3 ${isNew ? "border-success" : ""}`}
              style={isNew ? { borderWidth: 2, background: "rgba(25,135,84,0.04)" } : undefined}
            >
              <div className="d-flex flex-wrap align-items-start gap-2 mb-2">
                <div className="me-auto">
                  <div className="fw-semibold fs-5">
                    {item.name}{" "}
                    {isNew && <span className="badge bg-success align-middle ms-1">New</span>}
                    {item.status === "archived" && (
                      <span className="badge bg-secondary align-middle ms-1">Archived</span>
                    )}
                  </div>
                  <div className="small">
                    <a href={`mailto:${item.email}`}>{item.email}</a>
                    {item.phone && (
                      <>
                        {" · "}
                        <a href={`tel:${item.phone.replace(/[^\d+]/g, "")}`}>{item.phone}</a>
                      </>
                    )}
                  </div>
                </div>
                <time className="small text-muted text-nowrap" dateTime={item.createdAt}>
                  {when(item.createdAt)}
                </time>
              </div>

              <p className="mb-3" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {item.message}
              </p>

              <div className="d-flex flex-wrap gap-2">
                <a
                  className="btn btn-sm btn-success"
                  href={replyLink(item)}
                  onClick={() => isNew && setStatus(item, "read")}
                >
                  Reply by email
                </a>
                {isNew ? (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatus(item, "read")}>
                    Mark as read
                  </button>
                ) : item.status === "read" ? (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatus(item, "new")}>
                    Mark as new
                  </button>
                ) : null}
                {item.status === "archived" ? (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatus(item, "read")}>
                    Move to inbox
                  </button>
                ) : (
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatus(item, "archived")}>
                    Archive
                  </button>
                )}
                <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => remove(item)}>
                  Delete
                </button>
              </div>
              {!item.emailed && (
                <div className="small text-warning-emphasis mt-2">
                  The email copy of this message didn't send; it's only here.
                </div>
              )}
            </article>
          )
        })}
    </div>
  )
}
